/* eslint-disable max-lines */
import { sortAsc } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { ErrorDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { NumberInputFormik, TextArea, TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { Loading } from '@strata/react-loading-animation';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import classNames from 'classnames';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { MedDirectionList } from 'components/MedDirectionList';
import { Table } from 'components/Table';
import { CREATE_ROUTE } from 'services/constants';
import { parseValidationError } from 'services/ErrorManagement';
import { handleNullish } from 'services/Helpers';
import { put, useGet } from 'services/Http';
import {
    loadProductConfigs,
    packageProductConfigs,
    productConfigSchema,
} from 'services/ProductConfigSupport';

const SEND_LDD_TASK = 'SEND_LDD_TASK';

export const ProductConfigDetails = () => {
    const { oid } = useParams();
    const navigate = useNavigate();
    const { enableGpiIntegrationList } = useFlags();

    const goHome = () => navigate('/product-config-list');

    const [lastError, setLastError] = useState(null);
    const [isError, errorTitle, errorDetails] = parseValidationError(lastError);

    const productConfig = useGet(`/reference/product_benefit_configs/edit/${oid}`);
    const nbdConfig = useGet(`/performance/admin/needs_by_date/config/${oid}`);
    const therapeuticCategoryList = useGet('/reference/therapeutic_categories/')?.sort(sortAsc);

    if (!productConfig || !therapeuticCategoryList || !nbdConfig) {
        return <Loading />;
    }

    function clearError() {
        setLastError(null);
    }

    const options = [
        { name: 'Yes', text: 'Yes', value: true },
        { name: 'No', text: 'No', value: false },
    ];
    const data = productConfig.payer_configs
        ? Array.from(productConfig.payer_configs).map((p) => [
              p.segment_id,
              p.preferred_billing_type,
              p.is_active ? 'YES' : 'NO',
              p.use_drug_qty_defaults ? 'YES' : 'NO',
          ])
        : [];

    return (
        <Formik
            initialValues={{
                ...loadProductConfigs(oid, productConfig),
                needs_by_date_days: nbdConfig.needs_by_date_days,
            }}
            validationSchema={productConfigSchema(enableGpiIntegrationList)}
            onSubmit={async (values) => {
                try {
                    const body = packageProductConfigs(values, enableGpiIntegrationList);
                    await Promise.all([
                        put(`/reference/product_benefit_configs/${values.oid}`, body),
                        put(`/performance/admin/needs_by_date/config/${values.oid}`, {
                            needs_by_date_days: handleNullish(values.needs_by_date_days, undefined),
                        }),
                    ]);
                    SuccessToast({
                        message: values.send_ldd_task
                            ? 'Product configurations saved successfuly, and worklist task sent.'
                            : 'Product configuration saved successfully.',
                    });
                    goHome();
                } catch (err) {
                    setLastError(err);
                }
            }}
        >
            {(formik) => {
                const disableSave = !formik.dirty || formik.isSubmitting || !formik.isValid;
                return (
                    <>
                        <ErrorDialog
                            dialogTitle={errorTitle}
                            isOpen={isError}
                            onSecondaryButtonClick={() => clearError()}
                            dialogContent={errorDetails}
                        />
                        <FormContainer
                            formTitle="Edit Product Config"
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={disableSave}
                            onSecondaryButtonClick={goHome}
                            formLeftContent={
                                <>
                                    <TextInputFormik
                                        label="Product Service OID"
                                        id="oid"
                                        disabled
                                        required
                                    />
                                    <NumberInputFormik
                                        label="HC/PCS Billing Unit"
                                        id="hcpcs_billing_unit"
                                        required
                                        maxDecimalPlaces={4}
                                        step="0.0001"
                                    />
                                    <TextInputFormik
                                        label="HC/PCS Billing Label"
                                        id="hcpcs_billing_label"
                                        required
                                    />
                                    <NumberInputFormik
                                        label="HC/PCS Package Size"
                                        id="hcpcs_package_size"
                                        required
                                        maxDecimalPlaces={4}
                                        step="0.0001"
                                    />
                                    <NumberInputFormik
                                        label="HC/PCS Conversion Rate"
                                        id="hcpcs_conversion_rate"
                                        required
                                        maxDecimalPlaces={10}
                                        step="0.0000000001"
                                    />
                                    <NumberInputFormik
                                        label="NDC Billing Unit"
                                        id="ndc_billing_unit"
                                        required
                                        maxDecimalPlaces={4}
                                        step="0.0001"
                                    />
                                    <TextInputFormik
                                        label="NDC Billing Label"
                                        id="ndc_billing_label"
                                        required
                                    />
                                    <NumberInputFormik
                                        label="NDC Package Size"
                                        id="ndc_package_size"
                                        required
                                        maxDecimalPlaces={4}
                                        step="0.0001"
                                    />
                                    {enableGpiIntegrationList && (
                                        <TextAreaFormik
                                            label="Integration GPIs"
                                            fill
                                            required
                                            id="integration_gpis"
                                            name="integration_gpis"
                                            helperText="Comma delimited Integration GPI list."
                                        />
                                    )}
                                    {!enableGpiIntegrationList && (
                                        <TextInputFormik
                                            label="Integration GPI"
                                            id="integration_gpi"
                                            required
                                        />
                                    )}
                                    <NumberInputFormik
                                        label="Default Quantity"
                                        id="drug_quantity_default"
                                        required
                                        maxDecimalPlaces={3}
                                        step="0.001"
                                    />
                                    <NumberInputFormik
                                        label="Default Days Supply"
                                        id="days_supply_default"
                                        required
                                    />
                                    <NumberInputFormik
                                        label="Needs By Date Default Days"
                                        id="needs_by_date_days"
                                        maxDecimalPlaces={0}
                                        helperText="Overrides the global default value of 7 days and is used to calculate the needs by date for first fills. Will be used for new starts and patients who are restarting therapy."
                                    />
                                    <SuggestSingleSelectFormik
                                        id="multistrength_flag"
                                        label="Multistrength?"
                                        required
                                        values={options}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'multistrength_flag',
                                                newValue.name === 'Yes',
                                            )
                                        }
                                        selectedItem={{
                                            name: formik.values.multistrength_flag ? 'Yes' : 'No',
                                        }}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="therapeutic_category"
                                        label="Therapeutic Category"
                                        required
                                        isLoading={!therapeuticCategoryList}
                                        values={therapeuticCategoryList}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('therapeutic_category', newValue)
                                        }
                                        selectedItem={therapeuticCategoryList?.find(
                                            (category) =>
                                                category === formik.values.therapeutic_category,
                                        )}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="fmh_specialty_list"
                                        label="FMH Specialty List"
                                        required
                                        values={[
                                            'FMH Core',
                                            'Extended',
                                            'Excluded - Non-Specialty',
                                            'Excluded - Cell/Gene Therapy',
                                        ]}
                                    />
                                    <SuggestSingleSelectFormik
                                        id="biologic_type"
                                        label="Biologic Type"
                                        required
                                        values={[
                                            'Not Biologic',
                                            'Biosimilar',
                                            'Interchangeable Biosimilar',
                                            'Reference Biologic',
                                        ]}
                                    />
                                    <RadioButtonGroupFormik
                                        name="is_ldd"
                                        label="LDD?"
                                        required
                                        options={options.toReversed()}
                                        onChange={(newValue) => {
                                            const isLdd = newValue === 'true';
                                            formik.setFieldValue('is_ldd', isLdd);
                                            if (!isLdd) {
                                                formik.setFieldValue('send_ldd_task', false);
                                                formik.setFieldValue('send_ldd_task_confirm', '');
                                            }
                                        }}
                                    />
                                    {formik.values.is_ldd && (
                                        <CheckboxListFormik
                                            name="send_ldd_task"
                                            label="SEND LDD WORKLIST TASK"
                                            options={[
                                                {
                                                    value: SEND_LDD_TASK,
                                                    text: 'Send to pharmacies without product capability to confirm access',
                                                },
                                            ]}
                                            values={
                                                formik.values.send_ldd_task ? [SEND_LDD_TASK] : []
                                            }
                                            onChange={(newValue) => {
                                                const sendLddTask =
                                                    newValue?.includes(SEND_LDD_TASK);
                                                formik.setFieldValue('send_ldd_task', sendLddTask);
                                                if (!sendLddTask) {
                                                    formik.setFieldValue(
                                                        'send_ldd_task_confirm',
                                                        '',
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                    {formik.values.send_ldd_task && (
                                        <>
                                            <div className="margin__bottom--small">
                                                <InlineMessage
                                                    intent={InlineMessage.Intent.WARNING}
                                                    wrapText
                                                    label='The worklist task will be sent after this form is saved. Type "CONFIRM" below.'
                                                />
                                            </div>
                                            <TextInputFormik
                                                id="send_ldd_task_confirm"
                                                placeholderText="TYPE CONFIRM"
                                            />
                                        </>
                                    )}
                                    <NumberInputFormik
                                        id="claim_lookback_days"
                                        label="Therapy Look Back Days"
                                        required
                                        maxDecimalPlaces={0}
                                        helperText="Number of days the system looks back for claims to determine if the patient is active on this therapy."
                                    />
                                    <RadioButtonGroupFormik
                                        name="is_brand_default"
                                        label="Product Type Default"
                                        required
                                        options={[
                                            {
                                                value: true,
                                                text: 'Brand',
                                            },
                                            { value: false, text: 'Generic' },
                                        ]}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'is_brand_default',
                                                newValue === 'true',
                                            )
                                        }
                                    />
                                    <NumberInputFormik
                                        label="Product Savings Multiplier"
                                        id="product_savings_multiplier"
                                        required
                                        maxDecimalPlaces={6}
                                        step="0.000001"
                                    />
                                    <MedDirectionList directions={formik.values.directions} />
                                    <TextArea
                                        label="Additional Med Options"
                                        fill
                                        id="additional_med_options"
                                        name="additional_med_options"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.additional_med_options}
                                    />
                                    {formik.dirty && (
                                        <div
                                            className={classNames(
                                                'padding__top--medium',
                                                'padding__bottom--large',
                                            )}
                                        >
                                            <InlineMessage
                                                intent={InlineMessage.Intent.ERROR}
                                                label="You have unsaved changes"
                                            />
                                        </div>
                                    )}
                                    <ListPageHeader
                                        pageTitle="Payer Configurations"
                                        buttons={[
                                            {
                                                label: 'Create Payer Config',
                                                path: `/product-payer-config/${oid}/${CREATE_ROUTE}/${CREATE_ROUTE}`,
                                                disabled: formik.dirty,
                                            },
                                        ]}
                                    />
                                    <Table
                                        columnNames={[
                                            'Payer Segment',
                                            'Billing',
                                            'Active?',
                                            'Use Defaults',
                                        ]}
                                        tableData={data}
                                        editPath={`/product-payer-config/${oid}`}
                                        keyFunction={(array) => `${array[0]}/${array[1]}`}
                                        disabled={formik.dirty}
                                    />
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};
